import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

const TOOLTIP_GAP_PX = 4;
const TOOLTIP_VIEWPORT_INSET_PX = 8;
const TOOLTIP_MAX_WIDTH_PX = 240;

export interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
}

/**
 * Non-interactive hover/focus explanation (UI-LAW §4a). The panel portals to
 * the document so table and pane overflow never clips it.
 */
export function Tooltip({ children, content }: TooltipProps) {
  const tooltipId = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const place = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (anchor === null || tooltip === null) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const left = Math.min(
        Math.max(TOOLTIP_VIEWPORT_INSET_PX, rect.left),
        Math.max(
          TOOLTIP_VIEWPORT_INSET_PX,
          window.innerWidth - TOOLTIP_MAX_WIDTH_PX - TOOLTIP_VIEWPORT_INSET_PX,
        ),
      );
      // Dynamic viewport geometry cannot be represented by token CSS. Keep
      // the values out of React's style prop, matching DataTable's measured
      // spacer/height pattern.
      tooltip.style.left = `${String(left)}px`;

      if (rect.top >= TOOLTIP_MAX_WIDTH_PX / 4) {
        tooltip.style.top = "";
        tooltip.style.bottom = `${String(
          window.innerHeight - rect.top + TOOLTIP_GAP_PX,
        )}px`;
      } else {
        tooltip.style.bottom = "";
        tooltip.style.top = `${String(rect.bottom + TOOLTIP_GAP_PX)}px`;
      }
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen]);

  return (
    <>
      <span
        aria-describedby={isOpen ? tooltipId : undefined}
        className="marker-tooltip__anchor"
        onBlur={() => {
          setIsOpen(false);
        }}
        onFocus={() => {
          setIsOpen(true);
        }}
        onMouseEnter={() => {
          setIsOpen(true);
        }}
        onMouseLeave={() => {
          setIsOpen(false);
        }}
        ref={anchorRef}
        tabIndex={0}
      >
        {children}
      </span>
      {isOpen
        ? createPortal(
            <span
              className="marker-tooltip"
              id={tooltipId}
              ref={tooltipRef}
              role="tooltip"
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
